/**
 * Tunables for the Priorities layout (severity × momentum). Every number here
 * is provisional until the calibration pass on real trees. Pure constants
 * only — no UI imports — so the scoring module stays testable in node. Zone
 * colours live in zoneStyles.js.
 */
export const CONFIG = {
  // ── Severity ─────────────────────────────────────────────────────────
  // Raw severity at or above which a finding sits in the top row of the
  // field. Weights run 0.1–1.0 and reach starts at 1.0, so any
  // dissatisfaction-class signal clears this from a single conversation,
  // a Feature Request needs ~3 conversations, a Habit ~20.
  SEVERITY_HIGH: 0.8,
  // Multiplier when most of a finding's classified observations were said
  // by someone product-side (customerVoice === false).
  VOICE_PENALTY: 0.5,
  // Fallback weight for a signal with neither its own weight nor a category.
  DEFAULT_SEVERITY_WEIGHT: 0.3,

  // ── Momentum ─────────────────────────────────────────────────────────
  // A finding needs this many observations on the LARGER side of the
  // comparison (what it holds now, or what it normally held) before its
  // momentum counts at full strength; below it the score is scaled down
  // linearly (2 of 4 → half). Stops a 1→3 jump topping the board without
  // silencing an 8→0 drop.
  CONFIDENCE_FLOOR: 4,
  // "delta" mode (thin history): share change RELATIVE to the finding's
  // previous share that separates steady from rising/cooling — 0.3 = a
  // 30% move on its own share. Relative, not percentage points, so a
  // sub-topic holding 1% of a 20k-observation corpus and one holding 5%
  // of a 200-observation corpus read the same; absolute points shrink
  // with the size of the taxonomy and flattened the field on big tenants.
  DELTA_BAND_REL: 0.3,
  // …but a sub-topic must also beat its own chance wobble: the band is the
  // larger of DELTA_BAND_REL and this many standard deviations of the
  // sampling noise of its relative change (see scoreMomentum). 2 = the usual
  // "probably not chance" mark. Calibrated on shi (Jul vs Apr '26): with the
  // flat 0.3 band half of all sub-topics read as moving and 71% of those
  // with under 10 observations did; with this rule 9% rise, 9% cool, and the
  // big rows are unchanged because their noise sits under 0.3 anyway.
  DELTA_NOISE_SIGMAS: 2,
  // "history" mode: the same threshold, in swings (the finding's own EWMA
  // standard deviation).
  Z_BAND: 1.0,
  // History mode switches on when this many earlier periods carry data.
  MIN_HISTORY_PERIODS: 4,
  // Delta mode refuses to compare against a previous period thinner than
  // this many observations in total.
  MIN_PRIOR_TOTAL: 20,
  // And the CURRENT period needs at least this many observations in total
  // before any share in it means anything — one of two observations is a
  // 50% share, which is noise, not momentum. The layout's entry jump picks
  // the latest month that clears this.
  MIN_NOW_TOTAL: 20,
  // How far back the history series looks, in periods of the selected grain.
  HISTORY_MAX_PERIODS: 12,
  // EWMA smoothing — 0.5 means the last period carries half the weight of
  // the baseline; the one before it a quarter, and so on.
  EWMA_ALPHA: 0.5,
  // A finding whose share never swung would divide by ~0; this is the
  // smallest swing we pretend it has, as a fraction of its usual share
  // (0.15 = 15% of the baseline). Relative for the same reason as
  // DELTA_BAND_REL. Both floors are also held up by the share that
  // CONFIDENCE_FLOOR observations take of the corpus, so a hair-thin share
  // can't turn a one-observation wobble into a 300% move.
  SIGMA_FLOOR_REL: 0.15,
  // Suppress momentum when more than this share of a finding's observations
  // are dated by annotation time rather than a customer-side date.
  MAX_FALLBACK_DATED_SHARE: 0.5,
  // How many periods with data, the selected one included, the trend spans.
  // Delta mode compares the selected period with the FIRST period in this
  // window (2 = the previous period). History mode's baseline ignores it —
  // it always runs over HISTORY_MAX_PERIODS. The list's per-period columns,
  // its sparkline and the "rising in" check all use the same window.
  TREND_WINDOW: 2,
  TREND_WINDOW_OPTIONS: [2, 3, 6, 12],

  // ── Insights ─────────────────────────────────────────────────────────
  INSIGHTS: {
    // How many periods (at the selected grain) each sub-topic's trajectory
    // spans, the selected one included.
    PERIODS: 6,
    // Insights shown before "show all".
    SHOWN: 6,
    // No kind may fill the rail: beyond this many of one kind, the rest of
    // that kind queue behind every other kind (a corpus where every
    // sub-topic is a "quiet consensus" should still show its fires).
    MAX_PER_KIND: 2,
    // Severity at which "stakes" saturates to 1 (a Drop Off across ~4
    // conversations), and the momentum index at which a change saturates.
    STAKES_CEILING: 2.5,
    INDEX_CEILING: 3,
    // Consecutive cooling periods before a severe sub-topic reads "resolving".
    RESOLVING_MIN_PERIODS: 2,
    // Empty periods before a once-severe sub-topic reads "gone silent".
    SILENT_PERIODS: 2,
    // A topic needs this many sub-topics before concentration means anything;
    // it fires when one sub-topic holds this share of the topic's
    // observations, or this share of its sub-topics sit in Fires/Chronic.
    MIN_TOPIC_ROWS: 3,
    CONCENTRATION_TOP_SHARE: 0.5,
    CONCENTRATION_ZONE_SHARE: 0.6,
    // Observations a topic needs in a period for its leading category to
    // count (escalation compares the first and last such periods).
    MIN_CATEGORY_N: 4,
    // Breadth vs depth: a loud voice is many observations from few
    // conversations; a quiet consensus is one observation each from many.
    LOUD_MIN_N: 6,
    LOUD_MAX_DOCS: 2,
    QUIET_MIN_DOCS: 6,
    QUIET_DOC_RATIO: 0.9,
    BREADTH_MAGNITUDE: 0.5,
    // Wins have low severity by construction; a slipping win still deserves
    // this much stakes (or its paired pain's, if higher).
    WIN_STAKES_FLOOR: 0.5,
  },

  // ── Presentation ─────────────────────────────────────────────────────
  // The four cards above the field: the hottest priorities + the brightest wins.
  TOP_PANEL: { alerts: 2, wins: 2 },
  FIELD_LABELS: 6,
  // The field's x-axis is a momentum INDEX where the band edge is ±1 in
  // both modes; this is how far it draws.
  // The index is drawn compressed beyond the band (see xScale.js): 1, 2, 4,
  // 8, 16 sit at equal spacing, so this cap is where a bubble finally pins
  // to the edge. Linear it was 3, and everything past it piled up there.
  FIELD_X_MAX: 16,
};

// Priority order of the zones — the ranked list sorts by this first.
export const ZONE_ORDER = ["fires", "chronic", "emerging", "resolving", "background", "fading"];
